/**
 * PageShell + AppFooter — the cabinet portal's standardized page frame.
 * ---------------------------------------------------------------------------
 * Today headers are ad-hoc (4 competing header styles; the canonical
 * `layout/PageHeader` was dead code) and there is no app-level footer. PageShell
 * unifies the header into ONE component and adds a page-level meta footer, while
 * `AppFooter` (rendered once by MainLayout) gives every route a consistent
 * app-wide footer band with a back-to-top affordance.
 *
 *   <PageShell
 *     title="Catalogue Fournisseurs" description="TPRM — parc fournisseurs"
 *     icon={Building2} tone="cyan"
 *     breadcrumb={[{ label: 'TPRM', href: '/vendor-pro/cockpit' }, { label: 'Catalogue' }]}
 *     actions={<Button>Nouveau fournisseur</Button>}
 *     meta="Dernière mise à jour il y a 2 h"
 *   >
 *     …page content…
 *   </PageShell>
 *
 * `variant="plain"` renders the canonical `layout/PageHeader` (breadcrumb + tabs
 * + accent chip) instead of the gradient banner — same API, one component.
 */
import * as React from 'react'
import { Link } from 'react-router-dom'
import { ArrowUp, ChevronRight, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { GradientHeader, type GradientTone } from '@/components/ui/gradient'
import { PageHeader, type PageBreadcrumb, type PageTab } from '@/components/layout/PageHeader'

export type { PageBreadcrumb, PageTab }

// Map the PageHeader accent vocabulary → the GradientHeader tone vocabulary so
// callers can pass one `tone`/`accent` for either variant.
const ACCENT_TO_TONE: Record<
  NonNullable<React.ComponentProps<typeof PageHeader>['accent']>,
  GradientTone
> = {
  blue: 'blue',
  amber: 'amber',
  green: 'emerald',
  purple: 'purple',
  red: 'red',
  teal: 'teal',
  indigo: 'indigo',
  slate: 'slate',
}

export interface PageShellProps {
  title: React.ReactNode
  description?: React.ReactNode
  icon?: LucideIcon
  /** Gradient banner tone (variant="gradient"). */
  tone?: GradientTone
  /** PageHeader accent (variant="plain"); also maps to a tone if `tone` unset. */
  accent?: NonNullable<React.ComponentProps<typeof PageHeader>['accent']>
  actions?: React.ReactNode
  breadcrumb?: PageBreadcrumb[]
  tabs?: PageTab[]
  badge?: string
  variant?: 'gradient' | 'plain'
  compact?: boolean
  /** Page-level footer meta line (e.g. last-updated). */
  meta?: React.ReactNode
  /** Set false to omit the page-level footer entirely. */
  showFooter?: boolean
  className?: string
  headerClassName?: string
  children?: React.ReactNode
}

export function PageShell({
  title,
  description,
  icon: Icon,
  tone,
  accent = 'blue',
  actions,
  breadcrumb,
  tabs,
  badge,
  variant = 'gradient',
  compact,
  meta,
  showFooter = true,
  className,
  headerClassName,
  children,
}: PageShellProps) {
  const resolvedTone = tone ?? ACCENT_TO_TONE[accent]

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {variant === 'plain' ? (
        <PageHeader
          title={typeof title === 'string' ? title : String(title)}
          subtitle={typeof description === 'string' ? description : undefined}
          icon={Icon}
          accent={accent}
          badge={badge}
          breadcrumb={breadcrumb}
          tabs={tabs}
          actions={actions}
          className={headerClassName}
        />
      ) : (
        <div className={cn('flex flex-col gap-2', headerClassName)}>
          {breadcrumb && breadcrumb.length > 0 && (
            <ol className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground" aria-label="Breadcrumb">
              {breadcrumb.map((crumb, idx) => (
                <li key={idx} className="flex items-center gap-1">
                  {idx > 0 && <ChevronRight className="h-3 w-3 opacity-50" />}
                  {crumb.href ? (
                    <Link to={crumb.href} className="hover:text-foreground hover:underline">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className={cn(idx === breadcrumb.length - 1 && 'font-medium text-foreground')}>
                      {crumb.label}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          )}
          <GradientHeader
            title={title}
            subtitle={description}
            icon={Icon}
            tone={resolvedTone}
            actions={actions}
            compact={compact}
          />
          {tabs && tabs.length > 0 && (
            <PageHeader title="" tabs={tabs} className="!mb-0 !gap-0 !border-b-0 !pb-0" />
          )}
        </div>
      )}

      {children}

      {showFooter && (meta || true) && <PageFooter meta={meta} />}
    </div>
  )
}

/** Page-level footer meta strip (rendered inside PageShell). */
export function PageFooter({ meta, className }: { meta?: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-border/50 pt-3 text-[11px] text-muted-foreground',
        className,
      )}
    >
      <span>{meta}</span>
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium transition-colors hover:bg-accent hover:text-foreground"
      >
        <ArrowUp className="h-3 w-3" /> Haut de page
      </button>
    </div>
  )
}

/**
 * AppFooter — the app-wide footer band rendered once by MainLayout so every
 * route has a consistent footer (brand, quick links, environment).
 */
export function AppFooter() {
  const year = new Date().getFullYear()
  return (
    <footer className="shrink-0 border-t border-border/60 bg-card/60 px-4 py-2.5 backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br from-primary to-blue-400 text-[8px] font-black text-white">
            AC
          </span>
          <span className="font-semibold text-foreground/80">AdminClient</span>
          <span className="text-muted-foreground">© {year} SGRC — Cabinet GRC</span>
        </div>
        <nav className="flex flex-wrap items-center gap-3">
          <Link to="/support" className="hover:text-foreground hover:underline">Support</Link>
          <Link to="/legal" className="hover:text-foreground hover:underline">Juridique</Link>
          <Link to="/settings/privacy" className="hover:text-foreground hover:underline">Confidentialité</Link>
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center gap-1 hover:text-foreground"
          >
            <ArrowUp className="h-3 w-3" /> Haut
          </button>
        </nav>
      </div>
    </footer>
  )
}

export default PageShell
